import type { ArchiveResult } from './trash'
import { api } from './client'

export interface Department {
  id: number
  name: string
  sortOrder: number
  isActive: boolean
  hasOperators?: boolean
  /** ADR-019 (Equal Point): il ramo del settore; null se non è ancora associato. */
  businessLineId?: number | null
  createdAt: string
}

export interface AgentProfile {
  id: number
  userId: number
  agencyId?: number
  departmentId?: number
  categoryId?: number
  code?: string
  isActive: boolean
  createdAt: string
}

/**
 * TASK-0072: shape returned by `GET /api/agents/{id}` to an Admin caller
 * (`AgentsController.ToDetailResponseAsync`, RSK-009/TASK-0010/TASK-0012 —
 * Iban/VatNumber/FiscalCode/CommissionPercentage/Notes are gated Admin-only
 * on read). `Create`/`Update` on this controller are already
 * `[Authorize(PolicyNames.Admin)]`-gated at the whole-action level, so
 * whoever can reach the create/edit UI already has Admin rights and this
 * shape is exactly what they're allowed to see. Deliberately a separate
 * type from `AgentProfile` (the `List` item shape) rather than extending
 * it: `List` stays reachable by any `EqualStaff` caller and must keep
 * excluding these fields, only the single-record `Get` exposes them.
 * Used to prefill the edit form — `AgentsController.Update` is a
 * full-replace PUT (`AgentProfile.Update` overwrites every field,
 * including with `null`), so submitting an edit without first loading
 * these fields would silently blank them out on save even though the UI
 * never touched them.
 */
export interface AgentDetail extends AgentProfile {
  vatNumber?: string
  fiscalCode?: string
  iban?: string
  commissionPercentage?: number
  notes?: string
  updatedAt: string
}

export interface AgentRequestBody {
  userId: number
  agencyId?: number
  departmentId?: number
  categoryId?: number
  code?: string
  vatNumber?: string
  fiscalCode?: string
  iban?: string
  commissionPercentage?: number
  notes?: string
}

export interface UserCategory {
  id: number
  name: string
  slug: string
  color?: string
  description?: string
  isSystem: boolean
  isActive: boolean
  sortOrder?: number
  createdAt: string
  canSeeAllContracts?: boolean
  canSeeSubordinates?: boolean
  canSeeRealAmounts?: boolean
  canSeeTotalCommissions?: boolean
  canManageUsers?: boolean
  canManageProducts?: boolean
  canManagePipelines?: boolean
  canManageForms?: boolean
}

export interface UserCategoryPermissionFields {
  canSeeAllContracts?: boolean
  canSeeSubordinates?: boolean
  canSeeRealAmounts?: boolean
  canSeeTotalCommissions?: boolean
  canManageUsers?: boolean
  canManageProducts?: boolean
  canManagePipelines?: boolean
  canManageForms?: boolean
}

export const departmentsApi = {
  list: () => api<Department[]>('/api/departments'),
  create: (name: string, sortOrder?: number, hasOperators?: boolean) =>
    api<{ id: number }>('/api/departments', { method: 'POST', body: JSON.stringify({ name, sortOrder, hasOperators }) }),
  update: (id: number, data: { name?: string; sortOrder?: number; hasOperators?: boolean }) =>
    api<void>(`/api/departments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) =>
    api<void>(`/api/departments/${id}`, { method: 'DELETE' }),
}

export const agentsApi = {
  list: (p?: { departmentId?: number; isActive?: boolean; page?: number }) => {
    const q = new URLSearchParams()
    if (p?.departmentId) q.set('departmentId', String(p.departmentId))
    if (p?.isActive !== undefined) q.set('isActive', String(p.isActive))
    q.set('page', String(p?.page ?? 1))
    q.set('size', '25')
    return api<{ total: number; page: number; size: number; items: AgentProfile[] }>(`/api/agents?${q}`)
  },
  get: (id: number) => api<AgentDetail>(`/api/agents/${id}`),
  /** Il profilo agente di un utente; null se l'utente non è (ancora) un agente. */
  getByUser: (userId: number) => api<AgentDetail>(`/api/agents/by-user/${userId}`).catch(() => null),
  create: (data: AgentRequestBody) =>
    api<{ id: number }>('/api/agents', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: AgentRequestBody) =>
    api<void>(`/api/agents/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  /** F-104: il profilo non si cancella, si disattiva: `200 { archived: true }`. */
  delete: (id: number) =>
    api<ArchiveResult | undefined>(`/api/agents/${id}`, { method: 'DELETE' }),
}

export const userCategoriesApi = {
  /** F-163: `includeInactive` mostra anche le categorie inattive (create dai template), per poterle attivare. */
  list: (opts?: { includeInactive?: boolean }) =>
    api<UserCategory[]>(`/api/usercategories${opts?.includeInactive ? '?includeInactive=true' : ''}`),
  /** F-163: copia del ruolo; la copia nasce inattiva e mai di sistema (409 se lo slug esiste). */
  clone: (id: number, data: { name: string; slug: string }) =>
    api<{ id: number }>(`/api/usercategories/${id}/clone`, { method: 'POST', body: JSON.stringify(data) }),
  create: (
    data: { name: string; slug: string; description?: string; color?: string; sortOrder?: number } &
      UserCategoryPermissionFields,
  ) =>
    api<{ id: number }>('/api/usercategories', { method: 'POST', body: JSON.stringify(data) }),
  update: (
    id: number,
    data: { name?: string; slug?: string; description?: string; color?: string; sortOrder?: number; isActive?: boolean } &
      UserCategoryPermissionFields,
  ) =>
    api<void>(`/api/usercategories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) =>
    api<void>(`/api/usercategories/${id}`, { method: 'DELETE' }),
}
